import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Loader2, Save } from "lucide-react";

export default function AdminCompanies() {
  const [companies, setCompanies] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [modules, setModules] = useState([]);
  const [ref, setRef] = useState({ countries: [], currencies: [] });
  const [cfg, setCfg] = useState({});
  const [group, setGroup] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({}); // company_id -> modules[]
  const [form, setForm] = useState({ legal_name: "", trade_name: "", country_code: "BF", sector: "commerce", base_currency: "XOF", modules: [] });

  const load = () => api.get("/admin/companies").then(({ data }) => {
    setCompanies(data.companies); setSectors(data.sectors || []); setModules(data.modules || []);
    const d = {}; data.companies.forEach((c) => { d[c.id] = c.modules || []; }); setDraft(d);
  }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => {
    load();
    api.get("/reference").then(({ data }) => { setRef({ countries: data.countries || [], currencies: data.currencies || [] }); setCfg(data.country_config || {}); }).catch(() => {});
    api.get("/admin/group-overview").then(({ data }) => setGroup(data)).catch(() => {});
  }, []);
  const nf = (n) => Math.round(n || 0).toLocaleString("fr-FR");

  const toggleFormMod = (code) => setForm((f) => ({ ...f, modules: f.modules.includes(code) ? f.modules.filter((m) => m !== code) : [...f.modules, code] }));
  const sectorDefaults = () => setForm((f) => ({ ...f, modules: modules.filter((m) => m.default).map((m) => m.code) }));

  const create = async () => {
    if (!form.legal_name) return toast.error("Nom légal requis");
    setSaving(true);
    try {
      await api.post("/admin/companies", { ...form, modules: form.modules.length ? form.modules : modules.filter((m) => m.default).map((m) => m.code) });
      toast.success("Entreprise créée"); setOpen(false);
      setForm({ legal_name: "", trade_name: "", country_code: "BF", sector: "commerce", base_currency: "XOF", modules: [] });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const toggleDraft = (cid, code) => setDraft((d) => ({ ...d, [cid]: d[cid].includes(code) ? d[cid].filter((m) => m !== code) : [...d[cid], code] }));
  const saveModules = async (cid) => {
    try { await api.patch(`/admin/companies/${cid}/modules`, { modules: draft[cid] }); toast.success("Modules mis à jour"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!companies) return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div data-testid="admin-page" className="space-y-5">
      <PageHeader title="Administration — Entreprises" subtitle="Créez des entreprises et activez les modules selon leur secteur d'activité."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full gap-1.5" data-testid="new-company-button"><Plus className="w-4 h-4" /> Nouvelle entreprise</Button></DialogTrigger>
            <DialogContent className="bg-popover max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Nouvelle entreprise</DialogTitle><DialogDescription>Renseignez l'entreprise et choisissez ses modules.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Nom légal *</Label><Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="company-legal-name" /></div>
                  <div className="space-y-1"><Label>Nom commercial</Label><Input value={form.trade_name} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} data-testid="company-trade-name" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1"><Label>Secteur</Label>
                    <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} data-testid="company-sector">
                      {sectors.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1"><Label>Pays</Label>
                    <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.country_code} onChange={(e) => { const cc = e.target.value; setForm({ ...form, country_code: cc, base_currency: cfg[cc]?.currency || form.base_currency }); }} data-testid="company-country">
                      {ref.countries.map((c) => <option key={c.code} value={c.code}>{c.name || c.code}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1"><Label>Devise</Label>
                    <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.base_currency} onChange={(e) => setForm({ ...form, base_currency: e.target.value })} data-testid="company-currency">
                      {ref.currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                    </select>
                  </div>
                </div>
                {cfg[form.country_code] && (
                  <p className="text-xs text-muted-foreground" data-testid="country-config-hint">
                    Auto-configuré : TVA {cfg[form.country_code].vat_rate}% · CNSS {Math.round(cfg[form.country_code].cnss_rate * 100)}% · IUTS {Math.round(cfg[form.country_code].iuts_rate * 100)}% · Mobile Money : {cfg[form.country_code].mobile_money.join(", ")}
                  </p>
                )}
                <div className="space-y-1">
                  <div className="flex items-center justify-between"><Label>Modules activés</Label>
                    <button type="button" onClick={sectorDefaults} className="text-xs text-primary hover:underline" data-testid="apply-defaults">Modules recommandés</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {modules.map((m) => (
                      <label key={m.code} className="flex items-center gap-2 text-sm bg-secondary rounded-md px-2.5 py-2 cursor-pointer">
                        <input type="checkbox" checked={form.modules.includes(m.code)} onChange={() => toggleFormMod(m.code)} data-testid={`form-mod-${m.code}`} />
                        {m.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} disabled={saving} className="rounded-full" data-testid="save-company-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer l'entreprise"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      {group && (
        <Card className="p-5 rounded-md border-border shadow-sm mb-5" data-testid="group-overview">
          <div className="font-display font-bold mb-3">Vue Groupe — {group.companies_count} entreprise(s)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <div className="bg-secondary rounded-md p-3"><div className="text-xs text-muted-foreground">CA consolidé (mois)</div><div className="font-display text-xl font-bold">{nf(group.revenue_month)}</div></div>
            <div className="bg-secondary rounded-md p-3"><div className="text-xs text-muted-foreground">Trésorerie consolidée</div><div className="font-display text-xl font-bold">{nf(group.treasury_total)}</div></div>
            <div className="bg-secondary rounded-md p-3"><div className="text-xs text-muted-foreground">Créances consolidées</div><div className="font-display text-xl font-bold text-rose-600">{nf(group.receivables)}</div></div>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-1.5">Entreprise</th><th>Pays</th><th className="text-right">CA mois</th><th className="text-right">Trésorerie</th><th className="text-right">Créances</th></tr></thead>
              <tbody>
                {group.companies.map((c) => (
                  <tr key={c.id} className="border-b border-border" data-testid={`group-row-${c.id}`}>
                    <td className="py-1.5 font-medium">{c.name}</td><td>{c.country} · {c.currency}</td>
                    <td className="text-right">{nf(c.revenue_month)}</td><td className="text-right">{nf(c.treasury)}</td><td className="text-right text-rose-600">{nf(c.receivables)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Montants nominaux (devises mixtes non converties).</p>
        </Card>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {companies.map((c) => (
          <Card key={c.id} className="p-5 rounded-md border-border shadow-sm" data-testid={`admin-company-${c.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-md bg-accent flex items-center justify-center"><Building2 className="w-5 h-5 text-primary" /></div>
              <div>
                <div className="font-display font-bold">{c.trade_name || c.legal_name}</div>
                <div className="text-xs text-muted-foreground">{c.sector} · {c.country_code} · {c.base_currency} · {c.users_count} membre(s) · {c.invoices_count} facture(s)</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {modules.map((m) => (
                <label key={m.code} className="flex items-center gap-2 text-sm bg-secondary rounded-md px-2.5 py-1.5 cursor-pointer">
                  <input type="checkbox" checked={draft[c.id]?.includes(m.code) || false} onChange={() => toggleDraft(c.id, m.code)} data-testid={`mod-${c.id}-${m.code}`} />
                  {m.name}
                </label>
              ))}
            </div>
            <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => saveModules(c.id)} data-testid={`save-modules-${c.id}`}><Save className="w-3.5 h-3.5" /> Enregistrer les modules</Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
